'use client';

import type { ProgramDetails } from '../../interface/Program';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState, type FC } from 'react';
import { useSession } from 'next-auth/react';
import {
  Avatar,
  Flex,
  Heading,
  Skeleton,
  Text,
} from '@eqi-investimentos/react';
import { Divider } from '@chakra-ui/react';
import { PATHS } from '@/config/paths';
import { OpenAccountDrawer } from '@/components/modules/OpenAccountDrawer';
import { CTAButton } from '@/modules/Content/ui/CTAButton';
import { SaveButton } from '@/components/elements/SaveButton';
import { IfRender } from '@/utils/jsx';
import { useGetCTAProgram } from '../../http/client/useGetCTAProgram';

export const ProgramInfoCard: FC<ProgramInfoCardProps> = ({
  author,
  bookmarked,
  id,
  isEqi,
  object,
  showOpenAccountButton,
  summary,
  thumb,
  title,
  slug,
}) => {
  const { data: session, status } = useSession();
  const hasSession = status === 'authenticated' && !session?.error;

  const { data: dataCTAProgram } = useGetCTAProgram(slug);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 800);
  }, []);

  return (
    <Flex
      justifyContent="space-between"
      p="l"
      w="full"
      bgColor="background.brandLight"
      borderRadius="xs"
    >
      <Flex
        gap="1.875rem"
        sx={{
          '.thumb_program': {
            w: '8.75rem',
            h: '8.75rem',
          },
        }}
      >
        <IfRender condition={isLoading}>
          <Skeleton w="8.75rem" h="8.75rem" borderRadius="0.3125rem" />
        </IfRender>
        <IfRender condition={!isLoading}>
          <Image
            className="thumb_program"
            src={thumb}
            alt={title}
            width={140}
            height={140}
            style={{ borderRadius: '0.3125rem', objectFit: 'cover' }}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/assets/eqi-placeholder.png';
            }}
          />
        </IfRender>
        <Flex
          flexDir="column"
          gap="2.5"
          sx={{
            '>div': { gap: 2.5 },
            '>div .chakra-text': { color: 'content.primary' },
            'button .chakra-text': { color: 'white' },
            '.chakra-avatar img': { objectFit: 'contain' },
          }}
        >
          <IfRender condition={isLoading}>
            <Skeleton w="250px" h="1.25rem" borderRadius="0.125rem" />
            <Skeleton w="400px" h="18px" borderRadius="0.125rem" />
          </IfRender>
          <IfRender condition={!isLoading}>
            <Heading
              as="h6"
              fontSize="1.25rem"
              fontWeight="medium"
              lineHeight="h6"
              color="content.primary"
            >
              {title}
            </Heading>
            <Text
              mb="2.5"
              size="xl"
              fontWeight="normal"
              lineHeight="1.1875rem"
              color="content.secondary"
              noOfLines={1}
            >
              {summary}
            </Text>
          </IfRender>

          <Flex>
            <IfRender condition={!!author?.name}>
              <Flex
                as={Link}
                href={`${PATHS.hubs.author}/${author?.slug}`}
                passHref
                rel="noopener noreferrer"
                sx={{
                  '.chakra-avatar': {
                    background: 'transparent !important',
                    w: '3rem',
                    h: '3rem',
                  },
                  '.chakra-text:nth-of-type(1)': {
                    color: 'content.primary',
                    fontSize: 'md',
                  },
                  '.chakra-text:nth-of-type(2)': {
                    color: 'content.secondary',
                    fontSize: 'sm',
                  },
                }}
              >
                <IfRender condition={isLoading}>
                  <Flex justifyContent="center" alignItems="center" gap="1rem">
                    <Skeleton w="3rem" h="3rem" borderRadius="5em" />
                    <Skeleton w="5rem" h="0.9375rem" borderRadius="0.1875rem" />
                  </Flex>
                </IfRender>
                <IfRender condition={!isLoading}>
                  <Avatar src={author?.thumb} name={author?.name} showLabels />
                </IfRender>
              </Flex>
            </IfRender>
            <IfRender
              condition={
                (!isEqi && showOpenAccountButton) ||
                (isEqi && !!dataCTAProgram?.data)
              }
            >
              <Divider
                mx="0.625rem"
                orientation="vertical"
                w="0.0625rem"
                backgroundColor="border.secondary"
              />
            </IfRender>
            {showOpenAccountButton && !isEqi && (
              <OpenAccountDrawer screen="research" />
            )}
            {isEqi && !!dataCTAProgram?.data && (
              <Flex h="3.25rem">
                <CTAButton data={dataCTAProgram.data} />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
      <IfRender condition={isLoading}>
        <Skeleton w="1.25rem" h="1.25rem" borderRadius="0.125rem" />
      </IfRender>
      <IfRender condition={!isLoading && hasSession}>
        <Flex flexDirection="column" justifyContent="start">
          <SaveButton {...{ id, object, bookmarked }} />
        </Flex>
      </IfRender>
    </Flex>
  );
};

type ProgramInfoCardProps = Pick<
  ProgramDetails,
  | 'author'
  | 'bookmarked'
  | 'id'
  | 'object'
  | 'summary'
  | 'thumb'
  | 'title'
  | 'slug'
> & {
  isEqi: boolean;
  showOpenAccountButton: ProgramDetails['show_open_account_button'];
};
